import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { LRUCache } from 'lru-cache';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Insight, InsightType } from 'src/interfaces/agent.interface';

// --- Payload types ---

export type CreateInsightPayload = {
  id: string;
  label?: string;
  instructions: string;
  type: InsightType;
  enum_options?: string[];
};

export type UpdateInsightPayload = {
  label?: string;
  instructions: string;
  type: InsightType;
  enum_options?: string[];
};

// --- Context interface ---

interface InsightsContextData {
  /** Returns cached insights for the given IDs; triggers a background fetch for any missing. */
  getByIds: (ids: string[]) => Insight[];
  loading: boolean;

  /** Create a new insight via the API and add it to the cache. */
  createInsight: (payload: CreateInsightPayload) => Promise<Insight>;

  /** Update an existing insight via the API and refresh the cache entry. */
  updateInsight: (
    id: string,
    payload: UpdateInsightPayload,
  ) => Promise<Insight>;

  /** List insights, optionally filtered by a search query. */
  listInsights: (query?: string) => Promise<Insight[]>;

  /** Get the number of agents that reference a given insight. */
  getAgentCountForInsight: (insightId: string) => Promise<number>;

  /** Delete an insight via the API and remove it from cache. */
  deleteInsight: (id: string) => Promise<void>;
}

const InsightsContext = createContext<InsightsContextData | null>(null);

const INSIGHTS_CACHE_MAX_SIZE = 100;

// --- Provider ---

interface InsightsProviderProps {
  children: ReactNode;
}

export function InsightsProvider({ children }: InsightsProviderProps) {
  const { get, patch, post, remove } = useAuthedRequest();

  const cacheRef = useRef(
    new LRUCache<string, Insight>({ max: INSIGHTS_CACHE_MAX_SIZE }),
  );
  const [, setCacheVersion] = useState(0);
  const [loading, setLoading] = useState(false);

  // Track in-flight fetches to avoid duplicate requests
  const inflightRef = useRef<Set<string>>(new Set());

  const fetchMissing = useCallback(
    async (missingIds: string[]) => {
      // Filter out IDs that are already being fetched
      const toFetch = missingIds.filter((id) => !inflightRef.current.has(id));
      if (toFetch.length === 0) return;

      toFetch.forEach((id) => inflightRef.current.add(id));
      setLoading(true);

      try {
        const query = new URLSearchParams({
          filters: JSON.stringify({ ids: toFetch }),
          size: INSIGHTS_CACHE_MAX_SIZE.toString(),
        });

        const response = await get(`/insights?${query.toString()}`);
        const insights: Insight[] = response.items || [];

        if (insights.length > 0) {
          insights.forEach((insight) => {
            cacheRef.current.set(insight.id, insight);
          });
          setCacheVersion((version) => version + 1);
        }
      } catch (error) {
        console.error('Failed to fetch insights:', error);
      } finally {
        toFetch.forEach((id) => inflightRef.current.delete(id));
        setLoading(false);
      }
    },
    [get],
  );

  const getByIds = useCallback(
    (ids: string[]): Insight[] => {
      const effectiveIds =
        ids.length > INSIGHTS_CACHE_MAX_SIZE
          ? ids.slice(0, INSIGHTS_CACHE_MAX_SIZE)
          : ids;

      if (ids.length > INSIGHTS_CACHE_MAX_SIZE) {
        console.warn(
          `getByIds received ${ids.length} insight IDs; processing only the first ${INSIGHTS_CACHE_MAX_SIZE}.`,
        );
      }

      const cachedInsights: Insight[] = [];
      const missing: string[] = [];

      effectiveIds.forEach((id) => {
        const cachedInsight = cacheRef.current.get(id);
        if (cachedInsight) {
          cachedInsights.push(cachedInsight);
        } else {
          missing.push(id);
        }
      });

      if (missing.length > 0) {
        // Fire-and-forget: triggers re-render when cache updates
        void fetchMissing(missing);
      }

      return cachedInsights;
    },
    [fetchMissing],
  );

  const createInsight = useCallback(
    async (payload: CreateInsightPayload): Promise<Insight> => {
      const createdInsight: Insight = await post('/insights', payload);

      cacheRef.current.set(createdInsight.id, createdInsight);
      setCacheVersion((version) => version + 1);

      return createdInsight;
    },
    [post],
  );

  const updateInsight = useCallback(
    async (id: string, payload: UpdateInsightPayload): Promise<Insight> => {
      const updatedInsight: Insight = await patch(
        `/insights/${encodeURIComponent(id)}`,
        payload,
      );

      cacheRef.current.set(updatedInsight.id, updatedInsight);
      setCacheVersion((version) => version + 1);

      return updatedInsight;
    },
    [patch],
  );

  const listInsights = useCallback(
    async (query?: string): Promise<Insight[]> => {
      const filters: Record<string, string> = {};
      if (query) {
        filters.search = query;
      }
      const params = new URLSearchParams({
        filters: JSON.stringify(filters),
        size: INSIGHTS_CACHE_MAX_SIZE.toString(),
      });

      const response = await get(`/insights?${params.toString()}`);
      return response.items || [];
    },
    [get],
  );

  const getAgentCountForInsight = useCallback(
    async (insightId: string): Promise<number> => {
      const params = new URLSearchParams({
        page: '1',
        size: '1',
        filters: JSON.stringify({ insight_id: insightId }),
      });

      const response = await get(`/agents?${params.toString()}`);
      return response.total ?? 0;
    },
    [get],
  );

  const deleteInsight = useCallback(
    async (id: string): Promise<void> => {
      await remove(`/insights/${encodeURIComponent(id)}`);
      cacheRef.current.delete(id);
      setCacheVersion((version) => version + 1);
    },
    [remove],
  );

  return (
    <InsightsContext.Provider
      value={{
        getByIds,
        loading,
        createInsight,
        updateInsight,
        listInsights,
        getAgentCountForInsight,
        deleteInsight,
      }}
    >
      {children}
    </InsightsContext.Provider>
  );
}

export function useInsights(): InsightsContextData {
  const context = useContext(InsightsContext);

  if (!context) {
    throw new Error('useInsights must be used within an InsightsProvider');
  }

  return context;
}
